import React, { useState } from "react";
import { Button, Field, TextInput, Select, DescriptionList, useConfirm } from "../../../../../ui";
import { Dialog, FormGrid } from "../ui-pending";
import { toast } from "react-toastify";
import axios from "axios";
import { RENEWAL_PAYMENT } from "../../../../../Api/api-constant";
import _ from "lodash";

const OTHER_PAYMENT_TYPES = [
  { value: "course_extension_fee", label: "Course Extension Fee" },
  { value: "sale_of_instrument", label: "Sale of Musical Instruments" },
  { value: "summer_camp", label: "Summer Camp" },
  { value: "asm_fees_online", label: "ASM Fees - Online" },
];

const PAYMENT_MODES = [
  { value: "", label: "Select Payment Mode" },
  { value: "Razorpay", label: "Razorpay" },
  { value: "Stripe", label: "Stripe" },
  { value: "PayPal", label: "PayPal" },
  { value: "Bank transaction", label: "Bank transaction" },
  { value: "UPI", label: "UPI" },
  { value: "Cash", label: "Cash" },
  { value: "POS", label: "POS" },
  { value: "Others", label: "Others" },
];

interface OtherPaymentProps {
  isOpen: boolean;
  toggle: () => void;
  studentId: string;
  profileData: any;
  isOffline?: boolean;
  onSuccess?: () => void;
}

const OtherPayment: React.FC<OtherPaymentProps> = ({
  isOpen,
  toggle,
  studentId,
  profileData,
  isOffline = false,
  onSuccess
}) => {
  const formatDateInput = (date: Date) => {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, "0");
    const d = `${date.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const [otherPayment, setOtherPayment] = useState<string>("course_extension_fee");
  const [otherPaymentAmount, setOtherPaymentAmount] = useState<string>("");
  const [otherPaymentDiscount, setOtherPaymentDiscount] = useState<string>("");
  const [otherPaymentRefNumber, setOtherPaymentRefNumber] = useState<string>("");
  const [otherPaymentMode, setOtherPaymentMode] = useState<string>("");
  const [otherPaymentModeOther, setOtherPaymentModeOther] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<string>(formatDateInput(new Date()));
  const [disabledBtn, setDisabledBtn] = useState<boolean>(false);
  const [centerList, setCenterList] = useState<any[]>([]);
  const [selectedCenter, setSelectedCenter] = useState<string>("");

  // Get cookie helper function
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  // Get token
  const getToken = () => {
    const jwtToken = getCookie("jwtToken");
    const authToken = localStorage.getItem('authToken');
    return {
      authorization: jwtToken || authToken || ''
    };
  };

  // Get login details
  const getLoginDetails = () => {
    return {
      studentName: localStorage.getItem('loginUser') || "Admin"
    };
  };

  // Get customer ID from profile data
  const getCustomerId = () => {
    return profileData?.zohoSubscriptionCustomerId || '';
  };

  const handleClose = () => {
    setCenterList([]);
    setSelectedCenter('');
    toggle();
  };

  const { confirm, dialog } = useConfirm();

  const handleSubmit = async () => {
    // Prevent double-click by disabling button immediately
    if (disabledBtn) {
      return;
    }

    const amt = parseFloat(otherPaymentAmount) || 0;
    if (amt < 1) {
      toast.error("Amount must be at least 1.");
      return;
    }
    if (!otherPaymentMode || (otherPaymentMode === "Others" && !otherPaymentModeOther.trim())) {
      toast.error("Please select or enter a payment mode.");
      return;
    }
    if (!invoiceDate) {
      toast.error("Please select invoice date.");
      return;
    }
    
    // Disable button before showing confirmation
    setDisabledBtn(true);
    
    const ok = await confirm({
      title: 'Record this payment?',
      message: 'Please confirm all details before proceeding. This action cannot be undone.',
      confirmLabel: 'Record Payment',
    });
    if (ok) {
      const disc = parseFloat(otherPaymentDiscount) || 0;
      const netAmount = amt - (amt * disc / 100);

      const token = getToken();
      const loginDetails = getLoginDetails();
      const customerId = getCustomerId();

      axios.post(`${RENEWAL_PAYMENT}/api/payment/other-payment`, {
        studentId: studentId,
        userId: profileData?.userId,
        currency: (profileData?.timezone?.toLowerCase() == "asia/kolkata" || profileData?.timezone?.toLowerCase() == "asia/calcutta") ? "INR" : "USD",
        firstName: profileData?.firstName,
        lastName: profileData?.lastName,
        name: (profileData?.firstName || "") + " " + (profileData?.lastName || ""),
        customer_id: customerId || '',
        gender: profileData?.gender,
        ageGroup: profileData?.ageGroup,
        studentType: profileData?.studentType,
        userType: profileData?.userType,
        isOffline: isOffline || false,
        centreId: isOffline ? selectedCenter : "",
        email: profileData?.emailId,
        countryCode: profileData?.countryCode,
        contact: profileData?.mobileNumber,
        otherPayment: otherPayment,
        amount: amt,
        totalPrice: netAmount,
        token: token['authorization'],
        discountPercent: disc,
        invoice_date : invoiceDate,
        discountedPrice: (amt * disc / 100),
        createdBy: loginDetails?.studentName || "Admin",
        state: isOffline ? (selectedCenter == "3" ? 'Maharashtra' : 'Tamil Nadu') : _.get(profileData, 'state'),
        refNumber: otherPaymentRefNumber,
        paymentMode: otherPaymentMode === "Others" ? otherPaymentModeOther : otherPaymentMode,
      }, {
        headers: {
          "Authorization": token['authorization'],
          "Content-type": "application/json; charset=UTF-8"
        }
      }).then((response) => {
        setDisabledBtn(false);
        handleClose();

        if(response.data){
          // Reset fields after success
          setOtherPayment('course_extension_fee');
          setOtherPaymentAmount("");
          setOtherPaymentDiscount("");
          setInvoiceDate(formatDateInput(new Date()));
          setOtherPaymentRefNumber("");
        setOtherPaymentMode("");
        setOtherPaymentModeOther("");
        if (isOffline) setSelectedCenter(centerList[0]?.centreId || "");
        toast.success("Payment recorded successfully");
        if (onSuccess) {
          onSuccess();
        }
        }else{
          toast.error("Failed to record payment. Something went wrong. Please try again.");
        }
      }).catch((error) => {
        setDisabledBtn(false);
        toast.error(error?.message || "Failed to record payment. Something went wrong. Please try again.");
      });
    } else {
      // Re-enable button if user cancels confirmation
      setDisabledBtn(false);
    }
  };

  const netAmountLabel = (() => {
    const amt = parseFloat(otherPaymentAmount) || 0;
    const disc = parseFloat(otherPaymentDiscount) || 0;
    const netAmount = amt - (amt * disc / 100);
    const currency = (profileData?.timezone?.toLowerCase() == "asia/kolkata" || profileData?.timezone?.toLowerCase() == "asia/calcutta") ? "INR" : "USD";
    const currencySymbol = currency === 'INR' ? '₹ ' : '$ ';
    const finalAmount = netAmount >= 0 ? netAmount.toFixed(2) : amt.toFixed(2);
    return currencySymbol + finalAmount;
  })();

  return (
    <>
    <Dialog
      size="xl"
      isOpen={isOpen}
      onClose={handleClose}
      title="Other payment"
      subtitle="Record a payment that isn't for classes, such as an extension fee or an instrument."
      actions={
        <Button
          variant="primary"
          loading={disabledBtn}
          onClick={handleSubmit}
        >
          {disabledBtn ? 'Recording...' : 'Record Payment'}
        </Button>
      }
    >
      <FormGrid min={240}>
        <Field label="Payment Type">
          {(ctl) => (
            <Select
              {...ctl}
              value={otherPayment}
              onChange={(e) => setOtherPayment(e.target.value)}
              options={OTHER_PAYMENT_TYPES}
            />
          )}
        </Field>

        <Field label="Amount">
          {(ctl) => (
            <TextInput
              {...ctl}
              type="text"
              numeric
              value={otherPaymentAmount}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only numbers and decimal point
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  setOtherPaymentAmount(value);
                }
              }}
              placeholder="Enter amount"
            />
          )}
        </Field>

        <Field label="Discount (%)">
          {(ctl) => (
            <TextInput
              {...ctl}
              type="text"
              numeric
              value={otherPaymentDiscount}
              onChange={(e) => {
                let val = e.target.value;
                // Allow only numbers and decimal point
                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                  if (val === "") {
                    setOtherPaymentDiscount("");
                  } else {
                    const numVal = parseFloat(val);
                    if (!isNaN(numVal) && numVal <= 100) {
                      setOtherPaymentDiscount(val);
                    } else if (numVal > 100) {
                      setOtherPaymentDiscount("100");
                    }
                  }
                }
              }}
              placeholder="Enter discount percentage"
            />
          )}
        </Field>

        <Field label="Invoice Date">
          {(ctl) => (
            <TextInput
              {...ctl}
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value || formatDateInput(new Date()))}
            />
          )}
        </Field>

        <Field label="Payment Reference Number">
          {(ctl) => (
            <TextInput
              {...ctl}
              type="text"
              value={otherPaymentRefNumber}
              onChange={e => setOtherPaymentRefNumber(e.target.value)}
              placeholder="Enter payment reference number"
            />
          )}
        </Field>

        <Field label="Payment Mode" required>
          {(ctl) => (
            <Select
              {...ctl}
              value={otherPaymentMode}
              onChange={e => setOtherPaymentMode(e.target.value)}
              options={PAYMENT_MODES}
            />
          )}
        </Field>

        {otherPaymentMode === "Others" && (
          <Field label="Other payment mode" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="text"
                value={otherPaymentModeOther}
                onChange={e => setOtherPaymentModeOther(e.target.value)}
                placeholder="Please enter other payment mode"
              />
            )}
          </Field>
        )}
      </FormGrid>

      <DescriptionList items={[{ label: 'Net Amount', value: <span className="ds-num ds-strong">{netAmountLabel}</span> }]} />
    </Dialog>
    {dialog}
    </>
  );
};

export default OtherPayment;

